/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getTotalPages } from "selectors/documentSelectors";
import {
  getNextThumbnailsInViewYPos,
  getPrevThumbnailsInViewYPos,
  getTotalHeightOfThumbnail
} from "selectors/pagedViewSelectors";
import * as constants from "constants/constants";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import ThumbnailViewCss from "thumbnails/ThumbnailView.css";
import { getShouldUseModernViewer, getShouldUseNewThumbnailsGrid } from "selectors/appSelectors";
import classNames from "classnames";
import { isHighContrastEnabled } from "selectors/highContrastSelectors";

export const getDefaultThumbnailHeight = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_HEIGHT_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_HEIGHT;
};

export const getDefaultThumbnailWidth = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_WIDTH_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_WIDTH;
};

export const getBackgroundColor = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAILS_VIEW_BACKGROUND_COLOR_MODERN_VIEWER;
  }
  return constants.THUMBNAILS_VIEW_BACKGROUND_COLOR;
};

export const getThumbnailNumberHeight = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_NUM_HEIGHT_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_NUM_HEIGHT;
};

export const getThumbnailMargin = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_MARGIN_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_MARGIN;
};

export const getThumbnailNumTopMargin = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_NUM_TOP_MARGIN_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_NUM_TOP_MARGIN;
};

export const getThumbnailNumBottomMargin = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_NUM_BOTTOM_MARGIN_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_NUM_BOTTOM_MARGIN;
};

export const getThumbnailPadding = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_PADDING_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_PADDING;
};

export const getThumbnailsTopPadding = stores => {
  if (getShouldUseModernViewer(stores)) {
    if (getShouldUseNewThumbnailsGrid(stores)) {
      return constants.THUMBNAILS_TOP_PADDING_NEW_GRID;
    }
    return constants.THUMBNAILS_TOP_PADDING_MODERN_VIEWER;
  }
  return constants.THUMBNAILS_TOP_PADDING;
};

export const getThumbnailsMinSpaceBetween = stores => {
  if (getShouldUseNewThumbnailsGrid(stores)) {
    return constants.THUMBNAILS_MIN_SPACE_BETWEEN_NEW_GRID;
  }
  return 0;
};

export const getThumbnailViewTopPadding = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_TOP_PADDING_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_TOP_PADDING;
};

export const getThumbnailNumberWidth = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_NUM_WIDTH_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_NUM_WIDTH;
};

export const getThumbnailBorderColor = (stores, pageIndex, currPageIndex) => {
  let thumbnailBorderColor = "transparent";
  if (pageIndex === currPageIndex) {
    thumbnailBorderColor = constants.THUMBNAIL_BORDER_COLOR_SELECTED_STATE;
  }
  return thumbnailBorderColor;
};

export const getThumbnailBackgroundColor = (stores, pageIndex, currPageIndex) => {
  let thumbnailBackgroundColor = "none";
  if (pageIndex === currPageIndex) {
    if (!getShouldUseModernViewer(stores)) {
      thumbnailBackgroundColor = constants.THUMBNAIL_BACKGROUND_COLOR;
    }
  }
  return thumbnailBackgroundColor;
};

export const getThumbnailContainerHeight = stores => {
  const height = getDefaultThumbnailHeight(stores);
  if (getShouldUseModernViewer(stores)) {
    return height;
  }
  return height + 2 * constants.THUMBNAIL_MARGIN + getThumbnailNumberHeight(stores);
};

export const getThumbnailContainerTotalHeight = stores =>
  getDefaultThumbnailHeight(stores) +
  2 * getThumbnailMargin(stores) +
  getThumbnailNumberHeight(stores) +
  getThumbnailNumTopMargin(stores) +
  getThumbnailNumBottomMargin(stores) +
  2 * getThumbnailPadding(stores) +
  2 * getThumbnailBorderSize(stores);

export const getContainerClassName = stores => {
  let className = ThumbnailViewCss.ThumbnailContainer;
  if (!getShouldUseModernViewer(stores)) {
    className = classNames(
      className,
      ThumbnailViewCss.ThumbnailContainerBorder,
      ThumbnailViewCss.ThumbnailContainerPadding
    );
  }
  return className;
};

export const getThumbnailBottomMargin = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_CONTAINER_BOTTOM_MARGIN_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_CONTAINER_BOTTOM_MARGIN;
};

export const getThumbnailBorderSize = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAIL_BORDER_SIZE_MODERN_VIEWER;
  }
  return constants.THUMBNAIL_BORDER_SIZE;
};

export const getClassName = stores => {
  if (getShouldUseModernViewer(stores)) {
    return null;
  }
  return ThumbnailViewCss.TopMargin;
};

export const getFeatureWidth = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAILS_FEATURE_WIDTH_MODERN_VIEWER;
  }
  return constants.THUMBNAILS_FEATURE_WIDTH;
};

export const getPageViewHeight = stores => {
  const totalThumbnailHeight = getTotalHeightOfThumbnail(stores);
  return ({ index }) => totalThumbnailHeight;
};

export const getMaxWidth = stores => {
  let maxWidth =
    getDefaultThumbnailHeight(stores) + 2 * getThumbnailBorderSize(stores) + constants.THUMBNAIL_OFFSET_SIZE;
  if (getShouldUseModernViewer(stores)) {
    return () => maxWidth + 2 * getThumbnailPadding(stores);
  }
  return () => maxWidth + getThumbnailNumberHeight(stores);
};

export const getBorderStyleForPageThumbnail = (stores, ownProps) => {
  let border;
  const { pageIndex, currPageIndex } = ownProps;
  if (getShouldUseModernViewer(stores)) {
    border = `${constants.THUMBNAIL_BORDER_SIZE_MODERN_VIEWER}px solid`;
    if (pageIndex === currPageIndex) {
      let borderColor = constants.THUMBNAIL_BORDER_COLOR_SELECTED_STATE;
      if (isHighContrastEnabled(stores)) {
        borderColor = "Highlight";
      }
      border = `${border} ${borderColor}`;
    } else {
      let borderColor = constants.THUMBNAIL_BORDER_COLOR_UNSELECTED_STATE;
      if (isHighContrastEnabled(stores)) {
        borderColor = "ButtonText";
      }
      border = `${border} ${borderColor}`;
    }
  }
  return border;
};

export const onKeyDown = (evt, stores) => {
  let isHandled = false;
  switch (evt.keyCode) {
    case codes.ARROW_DOWN:
    case codes.ARROW_RIGHT:
      stores.pagedViewState.goToNextPage();
      stores.pagedViewState.setThumbnailIndexForFocus();
      isHandled = true;
      break;
    case codes.ARROW_UP:
    case codes.ARROW_LEFT:
      stores.pagedViewState.goToPrevPage();
      stores.pagedViewState.setThumbnailIndexForFocus();
      isHandled = true;
      break;
    case codes.PAGE_UP:
      stores.pagedViewState.scrollThumbnailsView(getPrevThumbnailsInViewYPos(stores));
      isHandled = true;
      break;
    case codes.PAGE_DOWN:
      stores.pagedViewState.scrollThumbnailsView(getNextThumbnailsInViewYPos(stores));
      isHandled = true;
      break;
    case codes.HOME:
      stores.pagedViewState.goToPage(1);
      stores.pagedViewState.setThumbnailIndexForFocus();
      isHandled = true;
      break;
    case codes.END:
      stores.pagedViewState.goToPage(getTotalPages(stores));
      stores.pagedViewState.setThumbnailIndexForFocus();
      isHandled = true;
      break;
    case codes.TAB:
      stores.pagedViewState.unsetThumbnailIndexForFocus();
      break;
    default:
      break;
  }
  if (isHandled) {
    evt.stopPropagation();
    evt.preventDefault();
  }
};

export const getThumbnailsHeaderHeight = stores => {
  if (getShouldUseModernViewer(stores)) {
    return constants.THUMBNAILS_PAGES_HEADER_HEIGHT_MODERN_VIEWER;
  }
  return constants.THUMBNAILS_PAGES_HEADER_HEIGHT_CLASSIC_VIEWER;
};

export const getShouldFocusThumbnail = stores => stores.thumbnailStore.shouldFocusThumbnail;
